// @ts-check
import {
  getCurrentInstance,
  onBeforeUnmount,
  unref,
  watch,
} from 'vue';

/** @type {HTMLAudioElement | null} */
let globalAudio;

/**
 * @param {import('vue').MaybeRef<string>} url
 * @param {object} [options]
 * @param {number} [options.volume]
 * @param {number} [options.playbackRate]
 * @param {boolean} [options.interrupt]
 */
export function useSound(url, {
  volume = 1,
  playbackRate = 1,
  interrupt = true,
} = {}) {
  const initializeAudio = () => {
    if (!globalAudio) {
      globalAudio = document.createElement('audio');
      globalAudio.setAttribute('preload', 'auto');
      globalAudio.setAttribute('controls', 'none');
      globalAudio.style.display = 'none';
      globalAudio.volume = volume;
      globalAudio.playbackRate = playbackRate;
      document.body.appendChild(globalAudio);
    }

    const unrefUrl = unref(url);
    if (!globalAudio.src.endsWith(unrefUrl)) {
      globalAudio.src = unrefUrl;
    }
  };

  const play = () => {
    initializeAudio();
    if (!globalAudio) {
      return;
    }

    if (interrupt) {
      globalAudio.load();
    }

    globalAudio.play();
  };

  const pause = () => {
    globalAudio?.pause();
  };

  const stop = () => {
    globalAudio?.load();
  };

  watch(
    () => [url],
    () => {
      initializeAudio();
    },
  );

  if (getCurrentInstance()) {
    onBeforeUnmount(() => {
      if (globalAudio) {
        globalAudio?.remove();
        globalAudio = null;
      }
    });
  }

  return {
    pause,
    play,
    stop,
  };
}
